import React, { useState, useMemo, useCallback } from 'react';
import { getKTCPickIdFromPick } from '../../utils/ktcPickIds';

const ff = "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif";

const POSITIONS = ['QB', 'RB', 'WR', 'TE'];
const ROUND_LABELS = { 1: '1st', 2: '2nd', 3: '3rd', 4: '4th' };

function Avatar({ src, name, size = 32 }) {
  const [errored, setErrored] = useState(false);
  const showImg = src && !errored;
  const initial = (name || '?').charAt(0).toUpperCase();
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: showImg ? '#E5E5EA' : '#F5F5F7',
      border: '1px solid rgba(0,0,0,0.08)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0, overflow: 'hidden',
      fontSize: Math.floor(size * 0.42), fontWeight: 700, color: '#86868B',
      fontFamily: ff,
    }}>
      {showImg
        ? <img src={src} alt="" onError={() => setErrored(true)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : initial}
    </div>
  );
}

function playerAvatarUrl(sleeperId) {
  if (!sleeperId || !/^\d+$/.test(String(sleeperId))) return null;
  return `https://sleepercdn.com/content/nfl/players/thumb/${sleeperId}.jpg`;
}

// ── Partner picker ─────────────────────────────────────────────────────────────
function PartnerPicker({ teams, myRosterId, distributions, onSelect }) {
  return (
    <div>
      <div style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase', color: '#86868B', marginBottom: '16px', fontFamily: ff }}>
        Who are you trading with?
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {teams.filter(t => t.rosterId !== myRosterId).map(team => {
          const topPlayers = (team.players || [])
            .map(id => distributions[id])
            .filter(Boolean)
            .sort((a, b) => (b.ktcValueSF || 0) - (a.ktcValueSF || 0))
            .slice(0, 3);
          return (
            <div
              key={team.rosterId}
              onClick={() => onSelect(team)}
              style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '12px 16px', borderRadius: '14px', cursor: 'pointer',
                background: '#FFFFFF', border: '1px solid rgba(0,0,0,0.07)',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(0,0,0,0.16)'; e.currentTarget.style.background = '#FAFAFA'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(0,0,0,0.07)'; e.currentTarget.style.background = '#FFFFFF'; }}
            >
              <Avatar src={team.avatarUrl} name={team.displayName} size={40} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '15px', fontWeight: 600, color: '#1D1D1F', fontFamily: ff }}>{team.displayName}</div>
                <div style={{ fontSize: '12px', color: '#86868B', marginTop: '2px', fontFamily: ff, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {topPlayers.map(p => p.name).join(', ') || 'No roster data'}
                </div>
              </div>
              <div style={{ fontSize: '20px', color: '#C7C7CC', fontFamily: ff }}>›</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Roster column (one side of the trade) ─────────────────────────────────────
function RosterColumn({ team, distributions, gives, onToggle, activeTab, label, isSuperFlex, teamNameById }) {
  const ktcKey = isSuperFlex ? 'ktcValueSF' : 'ktcValue1QB';
  const players = useMemo(() => {
    if (!team?.players) return [];
    return team.players
      .map(id => ({ id, ...(distributions[id] || { name: id, position: 'UNK' }), sleeperId: id }))
      .filter(p => p.name && p.position === activeTab)
      // Sort by the league-appropriate KTC value (Superflex vs 1QB), highest first.
      .sort((a, b) => (b[ktcKey] || 0) - (a[ktcKey] || 0));
  }, [team, distributions, activeTab, ktcKey]);

  const picks = useMemo(() => {
    if (activeTab !== 'PICKS') return [];
    return (team?.picks || []).map(pk => ({
      id: `pick-${pk.season}-${pk.round}-${pk.originalRosterId}`,
      ...pk,
    }));
  }, [team, activeTab]);

  const items = activeTab === 'PICKS' ? picks : players;
  const isSelected = (id) => gives.some(g => g.id === id);

  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
        <Avatar src={team?.avatarUrl} name={team?.displayName} size={30} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: '#1D1D1F', fontFamily: ff, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{team?.displayName}</div>
          <div style={{ fontSize: '11px', color: '#86868B', fontFamily: ff }}>{label}</div>
        </div>
      </div>

      {items.length === 0 ? (
        <div style={{ fontSize: '13px', color: '#C7C7CC', padding: '8px 0', fontFamily: ff }}>None</div>
      ) : items.map(item => {
        const sel = isSelected(item.id);
        const isPlayer = activeTab !== 'PICKS';
        const viaTrade = !isPlayer && item.originalRosterId !== team?.rosterId;
        return (
          <div
            key={item.id}
            onClick={() => onToggle(item)}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: isPlayer ? '8px 10px' : '10px 12px',
              borderRadius: '10px', cursor: 'pointer', marginBottom: '4px',
              background: sel ? '#1D1D1F' : '#FFFFFF',
              border: sel ? '1px solid #1D1D1F' : '1px solid rgba(0,0,0,0.07)',
              transition: 'all 0.12s',
              fontFamily: ff,
            }}
          >
            {isPlayer && <Avatar src={playerAvatarUrl(item.sleeperId)} name={item.name} size={26} />}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: 500, color: sel ? '#FFFFFF' : '#1D1D1F', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {isPlayer
                  ? item.name
                  : `${item.season} ${ROUND_LABELS[item.round] || `${item.round}th`}`}
                {viaTrade && (
                  <span style={{ fontSize: '10px', color: sel ? 'rgba(255,255,255,0.5)' : '#86868B', marginLeft: '5px' }}>
                    via {teamNameById?.[item.originalRosterId] || 'trade'}
                  </span>
                )}
              </div>
              {isPlayer && item.nflTeam && (
                <div style={{ fontSize: '11px', color: sel ? 'rgba(255,255,255,0.5)' : '#86868B', marginTop: '1px' }}>{item.nflTeam}</div>
              )}
            </div>
            {sel && <span style={{ fontSize: '11px', color: '#FFFFFF', flexShrink: 0 }}>✓</span>}
          </div>
        );
      })}
    </div>
  );
}

// ── Main TradeSandbox ──────────────────────────────────────────────────────────
export default function TradeSandbox({
  bootstrap,
  myRosterId,
  results,
  onTradeChange,
  onClose,
  isSuperFlex,
}) {
  // Each team's expected draft slot from the sim → classify its picks early/mid/late
  // (top third of the draft = 'early', i.e. the best/most-valuable picks).
  const pickTier = useMemo(() => {
    const numTeams = bootstrap?.teams?.length || 12;
    const res = results?.teams || {};
    const expectedSlot = {};
    for (const t of (bootstrap?.teams || [])) {
      const probs = res[t.rosterId]?.draftSlotProbs;
      if (probs && probs.length) {
        let e = 0;
        probs.forEach((p, i) => { e += i * p; });
        expectedSlot[t.rosterId] = e;
      }
    }
    const third = numTeams / 3;
    return (pick) => {
      const slot = expectedSlot[pick.originalRosterId];
      if (slot == null) return 'mid';
      return slot < third ? 'early' : slot < 2 * third ? 'mid' : 'late';
    };
  }, [bootstrap, results]);
  const [step, setStep] = useState('partner'); // 'partner' | 'build'
  const [partner, setPartner] = useState(null);
  const [activeTab, setActiveTab] = useState('QB');
  const [myGives, setMyGives] = useState([]);
  const [theirGives, setTheirGives] = useState([]);

  const myTeam = bootstrap?.teams?.find(t => t.rosterId === myRosterId);
  const distributions = bootstrap?.playerDistributions || {};
  const teamNameById = useMemo(() => {
    const m = {};
    (bootstrap?.teams || []).forEach(t => { m[t.rosterId] = t.displayName; });
    return m;
  }, [bootstrap]);

  const selectedCount = (tab) => {
    if (tab === 'PICKS') return myGives.filter(g => g.season).length + theirGives.filter(g => g.season).length;
    return [...myGives, ...theirGives].filter(g => g.position === tab).length;
  };

  const toggleMy = useCallback((item) => {
    setMyGives(prev => prev.some(g => g.id === item.id) ? prev.filter(g => g.id !== item.id) : [...prev, item]);
  }, []);

  const toggleTheir = useCallback((item) => {
    setTheirGives(prev => prev.some(g => g.id === item.id) ? prev.filter(g => g.id !== item.id) : [...prev, item]);
  }, []);

  // Build the trade adjustment payload from current selections.
  // The parent (MonteCarloSimulator) owns the actual simulation + before/after view.
  const buildAdjustments = useCallback(() => {
    if (!partner) return [];
    return [{
      giveAway: myGives.filter(i => !i.season).map(i => ({
        playerId: i.id, fromRosterId: myRosterId, toRosterId: partner.rosterId,
      })),
      receive: theirGives.filter(i => !i.season).map(i => ({
        playerId: i.id, fromRosterId: partner.rosterId, toRosterId: myRosterId,
      })),
      picksGiven: myGives.filter(i => i.season).map(i => ({
        season: i.season, round: i.round, originalRosterId: i.originalRosterId,
        fromRosterId: myRosterId, toRosterId: partner.rosterId, ktcValue: 0,
      })),
      picksReceived: theirGives.filter(i => i.season).map(i => ({
        season: i.season, round: i.round, originalRosterId: i.originalRosterId,
        fromRosterId: partner.rosterId, toRosterId: myRosterId, ktcValue: 0,
      })),
    }];
  }, [myGives, theirGives, partner, myRosterId]);


  const hasItems = (myGives.length + theirGives.length) > 0;

  // KTC trade-calculator link, pre-filled with this trade + the league's format/TEP.
  const ktcLink = useMemo(() => {
    const format = isSuperFlex ? '2' : '1';
    const tepMap = { 0: '0', 0.5: '1', 1: '2', 1.5: '3' };
    const tep = tepMap[bootstrap?.scoringSettings?.bonus_rec_te || 0] || '0';
    const idsFor = (items) => items.map(i =>
      i.season ? getKTCPickIdFromPick({ season: i.season, round: i.round, tier: pickTier(i) }) : distributions[i.id]?.ktcId
    ).filter(Boolean);
    const teamOne = idsFor(myGives);
    const teamTwo = idsFor(theirGives);
    if (!teamOne.length && !teamTwo.length) return 'https://keeptradecut.com/dynasty-rankings';
    return `https://keeptradecut.com/trade-calculator?var=5&pickVal=0&teamOne=${teamOne.join('|')}&teamTwo=${teamTwo.join('|')}&format=${format}&isStartup=0&tep=${tep}`;
  }, [myGives, theirGives, distributions, isSuperFlex, bootstrap, pickTier]);

  const isNarrow = typeof window !== 'undefined' && window.innerWidth < 600;
  const cardStyle = { background: '#FFFFFF', borderRadius: '18px', border: '1px solid rgba(0,0,0,0.08)', padding: isNarrow ? '18px 16px' : '28px' };
  const closeBtn = (
    <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#C7C7CC', fontSize: '22px', cursor: 'pointer', padding: '0 4px', lineHeight: 1, fontFamily: ff }}>×</button>
  );

  if (step === 'partner') {
    return (
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ fontSize: '17px', fontWeight: 700, color: '#1D1D1F', fontFamily: ff }}>Simulate a trade</div>
          {closeBtn}
        </div>
        <PartnerPicker teams={bootstrap?.teams || []} myRosterId={myRosterId} distributions={distributions} onSelect={t => { setPartner(t); setStep('build'); }} />
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      {/* Header — actions drop to their own full-width row on narrow screens so
          'Change partner / vs X / Open in KTC / Simulate' never overlap. */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
            <button
              onClick={() => { setStep('partner'); setPartner(null); setMyGives([]); setTheirGives([]); onTradeChange([]); }}
              style={{ background: 'none', border: 'none', color: '#86868B', fontSize: '14px', cursor: 'pointer', padding: 0, fontFamily: ff, whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              ← {isNarrow ? 'Back' : 'Change partner'}
            </button>
            <div style={{ width: '1px', height: '14px', background: 'rgba(0,0,0,0.12)', flexShrink: 0 }} />
            <div style={{ fontSize: '15px', fontWeight: 700, color: '#1D1D1F', fontFamily: ff, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              vs {partner?.displayName}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexShrink: 0 }}>
            {!isNarrow && hasItems && (
              <a href={ktcLink} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: '12px', color: '#86868B', fontWeight: 600, textDecoration: 'none', fontFamily: ff, whiteSpace: 'nowrap' }}>
                Open in KTC ↗
              </a>
            )}
            {!isNarrow && hasItems && (
              <button
                onClick={() => { onTradeChange(buildAdjustments()); onClose(); }}
                style={{ background: '#000000', color: '#FFFFFF', border: 'none', borderRadius: '980px', padding: '10px 22px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', transition: 'opacity 0.2s', fontFamily: ff, whiteSpace: 'nowrap' }}
                onMouseEnter={e => (e.currentTarget.style.opacity = '0.85')}
                onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
              >
                Simulate this trade →
              </button>
            )}
            {closeBtn}
          </div>
        </div>
        {isNarrow && hasItems && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '14px' }}>
            <a href={ktcLink} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: '13px', color: '#86868B', fontWeight: 600, textDecoration: 'none', fontFamily: ff, whiteSpace: 'nowrap', flexShrink: 0 }}>
              Open in KTC ↗
            </a>
            <button
              onClick={() => { onTradeChange(buildAdjustments()); onClose(); }}
              style={{ flex: 1, background: '#000000', color: '#FFFFFF', border: 'none', borderRadius: '980px', padding: '12px 22px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: ff, whiteSpace: 'nowrap' }}
            >
              Simulate this trade →
            </button>
          </div>
        )}
      </div>

      {/* Position tabs */}
      <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', marginBottom: '20px', flexWrap: 'wrap' }}>
        {[...POSITIONS, 'PICKS'].map(tab => {
          const count = selectedCount(tab);
          const active = activeTab === tab;
          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '8px 18px', borderRadius: '980px', border: 'none',
                background: active ? '#1D1D1F' : '#F5F5F7',
                color: active ? '#FFFFFF' : '#6E6E73',
                fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                transition: 'all 0.15s', fontFamily: ff, position: 'relative',
              }}
            >
              {tab}
              {count > 0 && (
                <span style={{
                  position: 'absolute', top: '-5px', right: '-5px',
                  background: active ? '#FFFFFF' : '#1D1D1F', color: active ? '#1D1D1F' : '#FFFFFF',
                  borderRadius: '50%', width: '16px', height: '16px',
                  fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700,
                }}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Two-column roster selector */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: isNarrow ? '10px' : '20px', minWidth: 0 }}>
        <RosterColumn team={myTeam} distributions={distributions} gives={myGives} onToggle={toggleMy} activeTab={activeTab} label="You give" isSuperFlex={isSuperFlex} teamNameById={teamNameById} />
        <RosterColumn team={partner} distributions={distributions} gives={theirGives} onToggle={toggleTheir} activeTab={activeTab} label="You receive" isSuperFlex={isSuperFlex} teamNameById={teamNameById} />
      </div>

      {/* Running summary of the whole trade (across every position tab) */}
      {(myGives.length > 0 || theirGives.length > 0) && (
        <div style={{ marginTop: '20px', paddingTop: '18px', borderTop: '1px solid rgba(0,0,0,0.06)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: isNarrow ? '10px' : '20px' }}>
          {[
            { label: 'You give', items: myGives, toggle: toggleMy, color: '#c0392b', bg: 'rgba(255,59,48,0.08)' },
            { label: 'You receive', items: theirGives, toggle: toggleTheir, color: '#1a9640', bg: 'rgba(52,199,89,0.10)' },
          ].map((side) => (
            <div key={side.label} style={{ minWidth: 0 }}>
              <div style={{ fontSize: '9px', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: '#86868B', marginBottom: '8px', fontFamily: ff }}>{side.label}</div>
              {side.items.length === 0 ? (
                <div style={{ fontSize: '12px', color: '#C7C7CC', fontFamily: ff }}>Nothing yet</div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {side.items.map((it) => (
                    <span key={it.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: side.bg, color: side.color, borderRadius: '980px', padding: '4px 6px 4px 11px', fontSize: '12px', fontWeight: 600, fontFamily: ff }}>
                      {it.season ? `${it.season} ${ROUND_LABELS[it.round] || `${it.round}th`}` : (it.name || 'Player')}
                      <button onClick={() => side.toggle(it)} title="Remove" style={{ border: 'none', background: 'rgba(0,0,0,0.07)', color: side.color, borderRadius: '50%', width: '15px', height: '15px', fontSize: '10px', lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, flexShrink: 0 }}>×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

    </div>
  );
}
